import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

/**
 * This reducer stores "frozen" facet counts that are shared between the 
 * ProductListTopFilters and ProductListAttributeFilters components.
 * 
 * When a user selects a facet within an attribute type (e.g., Price Range),
 * we "pin" that type and snapshot its counts BEFORE the API returns new data.
 * This allows both filter components to show all available options with their
 * original counts (OR logic within the same filter type).
 */
export interface FrozenFacetCountsState {
    /** The attribute type ID that is currently "frozen" */
    pinnedTypeId: string | null;
    /** The frozen counts for each facet value ID within the pinned type */
    frozenCounts: Record<string, number>;
}

const initialState: FrozenFacetCountsState = {
    pinnedTypeId: null,
    frozenCounts: {},
};

const frozenFacetCountsReducer = createTypedReducerWithImmer(initialState, {
    "Custom/ProductList/SetFrozenFacetCounts": (
        draft: Draft<FrozenFacetCountsState>,
        action: {
            pinnedTypeId: string;
            frozenCounts: Record<string, number>;
        },
    ) => {
        draft.pinnedTypeId = action.pinnedTypeId;
        draft.frozenCounts = action.frozenCounts;
    },
    "Custom/ProductList/ClearFrozenFacetCounts": (draft: Draft<FrozenFacetCountsState>) => {
        draft.pinnedTypeId = null;
        draft.frozenCounts = {};
    },
});

// Register the reducer in the global reducers object
(reducers as any).frozenFacetCounts = frozenFacetCountsReducer;

// Type augmentation for ApplicationState
export type FrozenFacetCountsApplicationState = {
    frozenFacetCounts: FrozenFacetCountsState;
} & ApplicationState;

type Reducers = {
    frozenFacetCounts: typeof frozenFacetCountsReducer;
};

export type FrozenFacetCountsActions = Parameters<Reducers[keyof Reducers]>[1] | AnyAction;

// Action creators for easy dispatch
export const setFrozenFacetCounts = (pinnedTypeId: string, frozenCounts: Record<string, number>) => ({
    type: "Custom/ProductList/SetFrozenFacetCounts",
    pinnedTypeId,
    frozenCounts,
});

export const clearFrozenFacetCounts = () => ({
    type: "Custom/ProductList/ClearFrozenFacetCounts",
});

export default frozenFacetCountsReducer;
